import { useApolloClient } from '@apollo/client/react'
import { DocumentNode } from 'graphql'
import React, { useContext, useState } from 'react'
import { useIntl } from 'react-intl'
import { Redirect } from 'react-router-dom'

import { Maybe, Time, TowingOrderType, TypeIdentifier, User, UserRole } from '../../../common/types'
import { ALTimeChangeModal } from '../../../components/ALOrderSubForm/ALTimeChangeModal'
import { isPrepaymentAlreadyActive, PREPAYMENT_STATUS } from '../../../components/TowingCommon/getPrepaymentLabel'
import { BigButtonContent, BigButtonSection } from '../../../components/layout'
import { Modal } from '../../../components/Modal'
import { Loading } from '../../../components/responses'
import { Styling } from '../../../components/Styling'
import { maybeStartTrackingRecord } from '../../../components/TrackedJobs/util'
import { StateStore } from '../../StoreProvider'
import { TowingRecord } from '../../TowingRecordPage/ViewTowingRecord/types'
import { RejectType } from '../components/RejectOrder/types'
import { StatusType } from '../types'
import { errorMessage } from './errorMessage'
import { messages } from '../messages'
import { formatMessageSafe } from '../../../util/translateInputOptions'
import {
  ACCEPT_AND_ASSIGN_TOWING_ORDER,
  ACCEPT_SOS_SERVICE_ORDER,
  ACCEPT_TOWING_ORDER,
  APPROVE_TOWING_ORDER_ASSIGNMENT,
  CANCEL_TOWING_ORDER,
  CREATE_TOWING_RECORD_FROM_ORDER,
  REJECT_TOWING_ORDER,
} from './queries'
import { StatusChangeButton } from './StatusChangeButton'
import { TowingRecordFromOrderOutputResponse } from './TowingRecordFromOrderMutation'
import { TowingOrder } from './types'
import { TOWING_JOBS_OPEN_QUERY } from '../../StoreProvider/queries'
import { ApolloClient } from '@apollo/client'

interface ButtonsProps {
  status: StatusType
  type: TowingOrderType
  towingOrderId: number
  handleSuccess: (msg: string) => void
  handleError: (msg: string) => void
  openRejectPage: (type: RejectType) => void
  towingOrder: TowingOrder
}

export const StatusChangeButtons: React.FunctionComponent<ButtonsProps> = ({
  handleError,
  handleSuccess,
  status,
  towingOrderId,
  type,
  openRejectPage,
  towingOrder
}) => {
  const intl = useIntl()
  const formatMessage = formatMessageSafe(intl)
  const [visibleModal, setVisibleModal] = useState<boolean>(false)
  const [showPrepaymentConfirm, setShowPrepaymentConfirm] = useState<boolean>(false)
  const [createdTowingRecordId, setCreatedTowingRecordId] = useState<number | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const {
    state: { currentUser },
  } = useContext(StateStore)

  const isOwn = currentUser && currentUser.user_id === towingOrder.operatorId
  const isSos = type === TowingOrderType.sos
  const isMainUser = currentUser && (currentUser.role === UserRole.duty_officer || currentUser.role === UserRole.main_user)
  const isOpenAndMainUser =  status === 'open' && isMainUser

  const approveAndAssign = status === 'pending_acceptance'
  const approveAndLeaveOpen = status === 'pending_acceptance'
  const userAccept = isOwn && status === 'assigned'
  const anyAccept = status === 'open'
  const userReject = isOwn && status === 'assigned'
  const cancel = status === 'pending_acceptance'
  const beginWork = isOwn && status === 'accepted'
  const towingrecordCreated = status === 'towing_record_created'
  const cancelSos = isSos && (status === 'accepted' || status === 'assigned' || isOpenAndMainUser)

  const promptTime = 20

  const setTRid = (data: any) => {
    const res = data as TowingRecordFromOrderOutputResponse
    if (res.towingRecordFromOrder && res.towingRecordFromOrder.__typename === 'TowingRecordFromOrderSuccess') {
      setCreatedTowingRecordId(res.towingRecordFromOrder.towingRecord.id)
    }
  }

  const maybeStartGeolocationTracking = (data: any) => {
    const res = data as TowingRecordFromOrderOutputResponse
    if (res.towingRecordFromOrder && res.towingRecordFromOrder.__typename === 'TowingRecordFromOrderSuccess') {
      const towingRecord = res.towingRecordFromOrder.towingRecord
      maybeStartTrackingRecord(towingRecord as TowingRecord)
    }
  }

  const changeStatus = async (
    client: ApolloClient,
    updateMutation: DocumentNode,
    variables: object,
    resultIsTowingRecord?: boolean
  ) => {
    try {
      setLoading(true)
      const response = await client.mutate({
        refetchQueries: [TOWING_JOBS_OPEN_QUERY],
        variables,
        mutation: updateMutation,
      })

      const errorMsg = errorMessage(response.data as any)
      if (errorMsg) {
        handleError(errorMsg)
        setLoading(false)
      } else {
        if (resultIsTowingRecord) {
          setTRid(response.data)
          maybeStartGeolocationTracking(response.data)
        }
        handleSuccess(formatMessage(messages.statusChangeButtons_statusUpdatedSuccess))
        setLoading(false)
      }
    } catch (error) {
      handleError((error as any).message)
      setLoading(false)
    }
  }

  const getMinutesSinceOrdered = (date: Date, time: Time) => {
    if(!time){
      return 1;
    }
    const hourMin = time.split(':')
    const currentTime = Date.now()
    date.setHours(parseInt(hourMin[0]), parseInt(hourMin[1]))
    return Math.abs(currentTime - date.getTime()) / 60000
  }

  const isPrepaymentUnpaid = () => {
    if (!towingOrder.hasPrepayment) return false
    if (towingOrder.prepaymentStatus === PREPAYMENT_STATUS.DELETED) return false
    return !isPrepaymentAlreadyActive(towingOrder.prepaymentType, towingOrder.prepaymentStatus)
  }

  const handleBeginWork = () => {
    if (promptBeforeSave()) {
      setVisibleModal(true)
    } else {
      changeStatus(client, CREATE_TOWING_RECORD_FROM_ORDER, { towingOrderId }, true)
    }
  }

  const promptBeforeSave = () => {
    if (towingOrder.typeIdentifier !== TypeIdentifier.autoliitto) {
      return false
    }
    
    // This is a case when AL-order has been copied (type autoliitto, but no autoliitto_order exists)
    if (towingOrder.automobileAndTouringClubFinland === null) {
      return false
    }

    if (towingOrder.automobileAndTouringClubFinland && towingOrder.automobileAndTouringClubFinland.delayedTowingReason){
      return false
    }

    if (getMinutesSinceOrdered(towingOrder.jobDetails.towingDate, towingOrder.jobDetails.towingTime) >= promptTime) {
      return true
    }

    return false
  }

  const url = `/towing-record/${createdTowingRecordId}/start`

  const client = useApolloClient()
  return (
    <BigButtonSection>
      <Loading loading={loading} />
          {approveAndAssign && (
            <StatusChangeButton
              category="save"
              changeStatus={() => {
                const mutation =
                  type === TowingOrderType.sos ? ACCEPT_SOS_SERVICE_ORDER : ACCEPT_AND_ASSIGN_TOWING_ORDER
                const mutationVariables =
                  type === TowingOrderType.sos
                    ? toSosMutationVariables(towingOrderId, currentUser)
                    : { id: towingOrderId }
                changeStatus(client, mutation, mutationVariables)
              }}
            >
              <BigButtonContent>
                <Styling fontSize="m">{formatMessage(messages.statusChangeButtons_approveAndAssignLine1)}</Styling>
                <Styling fontSize="l">{formatMessage(messages.statusChangeButtons_approveAndAssignLine2)}</Styling>
              </BigButtonContent>
            </StatusChangeButton>
          )}
          {approveAndLeaveOpen && (
            <StatusChangeButton
              category="save"
              changeStatus={() => changeStatus(client, ACCEPT_TOWING_ORDER, { id: towingOrderId })}
            >
              <BigButtonContent>
                <Styling fontSize="m">{formatMessage(messages.statusChangeButtons_approveAndLeaveOpenLine1)}</Styling>
                <Styling fontSize="l">{formatMessage(messages.statusChangeButtons_approveAndLeaveOpenLine2)}</Styling>
              </BigButtonContent>
            </StatusChangeButton>
          )}
          {(userAccept || anyAccept) && (
            <StatusChangeButton
              category="save"
              label={formatMessage(messages.statusChangeButtons_acceptLabel)}
              dataTestId="accept-job-button"
              changeStatus={() => {
                const mutation =
                  type === TowingOrderType.sos ? ACCEPT_SOS_SERVICE_ORDER : APPROVE_TOWING_ORDER_ASSIGNMENT
                const mutationVariables =
                  type === TowingOrderType.sos
                    ? toSosMutationVariables(towingOrderId, currentUser)
                    : { id: towingOrderId }
                changeStatus(client, mutation, mutationVariables)
              }}
            />
          )}
          {userReject && (
            <StatusChangeButton
              category="cancelSecondary"
              label={formatMessage(messages.statusChangeButtons_rejectLabel)}
              changeStatus={() => {
                // if (type === TowingOrderType.sos) {
                //   openRejectPage('reject')
                //   return
                // }
                changeStatus(client, REJECT_TOWING_ORDER, { input: { id: towingOrderId } })
              }}
            />
          )}
          {cancel && (
            <StatusChangeButton
              category="cancelSecondary"
              label={formatMessage(messages.statusChangeButtons_rejectLabel)}
              changeStatus={() => {
                if (type === TowingOrderType.sos) {
                  openRejectPage('cancel-sos')
                  return
                }
                changeStatus(client, CANCEL_TOWING_ORDER, { input: { id: towingOrderId } })
              }}
            />
          )}
          {beginWork && (
            <StatusChangeButton
              category="save"
              label={formatMessage(messages.statusChangeButtons_beginWorkLabel)}
              dataTestId="start-work-button"
              changeStatus={() => {
                if (isPrepaymentUnpaid()) {
                  setShowPrepaymentConfirm(true)
                } else {
                  handleBeginWork()
                }
              }}
            />
          )}
          {cancelSos && (
            <StatusChangeButton
              category="cancelSecondary"
              label={formatMessage(messages.statusChangeButtons_cancelSosLabel)}
              changeStatus={() => {
                openRejectPage('cancel-sos')
              }}
            />
            )
          }
          {towingrecordCreated && createdTowingRecordId && <Redirect to={url} />}
          {showPrepaymentConfirm && (
            <Modal
              label={formatMessage(messages.statusChangeButtons_prepaymentModalLabel)}
              message={formatMessage(messages.statusChangeButtons_prepaymentModalMessage)}
              confirmLabel={formatMessage(messages.statusChangeButtons_prepaymentModalConfirm)}
              confirmButtonCategory="save"
              closeLabel={formatMessage(messages.statusChangeButtons_prepaymentModalCancel)}
              onConfirm={() => {
                setShowPrepaymentConfirm(false)
                handleBeginWork()
              }}
              close={() => setShowPrepaymentConfirm(false)}
            />
          )}
          {visibleModal && (
            <ALTimeChangeModal
              label={formatMessage(messages.statusChangeButtons_delayedStartLabel, { promptTime })}
              onChangeOnlyReason={() => {
                changeStatus(client, CREATE_TOWING_RECORD_FROM_ORDER, { towingOrderId }, true)
                setVisibleModal(false)
              }}
              onClose={() => {
                setVisibleModal(false)
              }}
              isVisibleTimeInput={false}
              useQueryOnly={true}
              towingOrder={towingOrder}
              buttonSectionHeader={formatMessage(messages.statusChangeButtons_delayedStartReasonHeader)}
            />
          )}
      </BigButtonSection>
  )
}

function toSosMutationVariables(towingOrderId: number, user: Maybe<User>) {
  if (!user) {
    return {
      input: {
        id: towingOrderId,
        acceptedByDriverDetails: {
          driverName: '',
        },
      },
    }
  }

  return {
    input: {
      id: towingOrderId,
      acceptedByDriverDetails: {
        driverName: user.realName,
      },
    },
  }
}
